import {
  useV2ListProjectReviewsQuery,
  V2ListProjectReviewsApiArg,
} from "@generated";
import { ProjectReview } from "../types";

interface Params {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc" | string;
  skip?: boolean;
}

type SortParam = NonNullable<V2ListProjectReviewsApiArg["sort"]>;

// The endpoint's own default, and the reason the page exists: the project
// whose last ticket just closed is the one worth reviewing today.
const DEFAULT_SORT = ["-last-completed-on"] as SortParam;

const useListProjectReviews = ({
  pageNumber,
  pageSize,
  sortBy,
  sortOrder,
  skip = false,
}: Params) => {
  let customError: string | null = null;
  let pageMeta = { recordCount: 0, pageCount: 0 };

  const sort = sortBy
    ? ([sortOrder === "asc" ? sortBy : `-${sortBy}`] as SortParam)
    : DEFAULT_SORT;

  const { data, error, isLoading, isFetching, refetch } =
    useV2ListProjectReviewsQuery(
      { page: { number: pageNumber, size: pageSize }, sort },
      { skip }
    );

  let reviews: ProjectReview[] = [];

  if (data?.data) {
    try {
      reviews = data.data.map((review) => {
        if (!review.id) {
          throw new Error("Missing project review id");
        }

        const {
          "project-name": projectName,
          "client-name": clientName,
          "project-status": projectStatus,
          "last-completed-on": lastCompletedOn,
          "services-tracked": servicesTracked,
          "services-settled": servicesSettled,
          "unsettled-services": unsettledServices,
          "services-partial": servicesPartial,
          "estimated-hours": estimatedHours,
          "actual-hours": actualHours,
          "delta-hours": deltaHours,
          "variance-percentage": variancePercentage,
        } = review.attributes;

        return {
          id: String(review.id),
          projectName: projectName ?? "",
          clientName: clientName ?? "",
          projectStatus: projectStatus ?? "",
          lastCompletedOn: lastCompletedOn ?? "",
          servicesTracked: servicesTracked ?? 0,
          servicesSettled: servicesSettled ?? 0,
          unsettledServices: unsettledServices ?? 0,
          servicesPartial: servicesPartial ?? 0,
          estimatedHours: estimatedHours ?? 0,
          actualHours: actualHours ?? 0,
          deltaHours: deltaHours ?? 0,
          // ?? would swallow the deliberate null the API sends when nothing
          // settled carried an estimate, which is not the same as 0% variance.
          variancePercentage,
        };
      });

      const { "record-count": recordCount, "page-count": pageCount } =
        data.meta ?? {};
      pageMeta = {
        recordCount: recordCount ?? 0,
        pageCount: pageCount ?? 0,
      };
    } catch (err) {
      customError =
        err instanceof Error ? err.message : "An unknown error occurred";
    }
  }

  return {
    reviews,
    isLoading,
    isFetching,
    error: error ?? customError,
    pageMeta,
    refetch,
  };
};

export default useListProjectReviews;
